import fixture from "./nclean-fixtures.json";
import type { DataContract, Snapshots } from "./data-contracts";
import type { Row } from "./types";

export const NCLEAN_AS_OF = "2026-10-20T15:00:00+03:00";
export const NCLEAN_PROPERTY = "nclean-katinkulta";
const common = {
  operating_unit_id: "north-coast",
  property_id: NCLEAN_PROPERTY,
  property_name: "HCR Katinkulta",
  snapshot_at: NCLEAN_AS_OF,
};

/** Reference values printed on all four supplied work-log forms. */
export const CLEANING_STANDARDS = [
  ["1–11", "4", 86],
  ["1–11", "6", 102],
  ["1–11", "8", 124],
  ["12–13", "4", 86],
  ["12–13", "6", 102],
  ["12–13", "8", 124],
  ["14–15", "6", 102],
  ["14–15", "8", 124],
  ["16–17", "4", 82],
  ["16–17", "6", 104],
  ["16–17", "8", 124],
  ["18", null, 104],
  ["19", null, 42],
  ["20", "4", 80],
  ["20", "6", 86],
  ["20", "8", 86],
  ["21", null, 70],
] as const;

export function elapsedMinutes(
  start: string | null,
  finish: string | null,
): number | null {
  if (!start || !finish) return null;
  const minutes = (Date.parse(finish) - Date.parse(start)) / 60000;
  return Number.isFinite(minutes) && minutes >= 0 ? minutes : null;
}
export function standardMinutes(
  area: number,
  capacity: string | null,
): number | null {
  const group =
    area <= 11
      ? "1–11"
      : area <= 13
        ? "12–13"
        : area <= 15
          ? "14–15"
          : area <= 17
            ? "16–17"
            : String(area);
  return (
    CLEANING_STANDARDS.find(
      ([g, beds]) => g === group && (beds === capacity || beds === null),
    )?.[2] ?? null
  );
}

export function createNcleanData(): Snapshots {
  const units = fixture.units.map((u) => ({
    ...common,
    snapshot_at: "2026-10-18T23:59:00+03:00",
    id: u.unit_id,
    ...u,
  }));
  const byUnit = new Map(fixture.units.map((u) => [u.unit_id, u]));
  const billing = fixture.quantities.map((q) => {
    const unit = byUnit.get(q.unit_id)!;
    return {
      ...common,
      snapshot_at: "2026-10-18T23:59:00+03:00",
      ...q,
      group_name: unit.group_name,
      category: unit.category,
      classification: unit.classification,
      payer: q.source_column === 1 ? "Property company" : "HCR Rentals",
      service_marker: q.interim ? "Interim cleaning" : "Source quantity",
      pack4_units: q.pack4_factor === null ? null : q.quantity * q.pack4_factor,
      pack2_units: q.pack2_factor === null ? null : q.quantity * q.pack2_factor,
    };
  });
  const logs: Row[] = [];
  for (const date of ["2026-10-17", "2026-10-20"]) {
    const paired = date === "2026-10-17";
    const rows = [
      ["18D2", "09:10", "10:00", "", ""],
      ["18D1", "10:03", "10:53", "", ""],
      ["18D2", "11:01", "12:11", "", "10-minute coffee break"],
      ["1A1", "12:13", "13:43", "", "30-minute lunch; very dirty"],
      ["9H2", "14:29", "15:30", "", "Assistance"],
      ["8F1", "15:32", null, "", "Assistance; finish not recorded"],
    ];
    rows.forEach(([unit, start, finish, vap, notes], i) => {
      const started = `${date}T${start}:00+03:00`,
        ended = finish ? `${date}T${finish}:00+03:00` : null;
      logs.push({
        ...common,
        id: `${date}-${i}`,
        work_date: date,
        team: paired ? "Team A" : "Cleaner B",
        cleaner_1: paired ? "Cleaner A" : "Cleaner B",
        cleaner_2: paired ? "Cleaner C" : null,
        unit_id: unit,
        vap,
        job_start: started,
        job_finish: ended,
        elapsed_minutes: elapsedMinutes(started, ended),
        notes,
      });
    });
  }
  return {
    mv_nclean_units: units,
    mv_nclean_billing: billing,
    mv_nclean_work_logs: logs,
    mv_nclean_standards: CLEANING_STANDARDS.map(
      ([area_group, capacity, standard_minutes], i) => ({
        ...common,
        id: `standard-${i}`,
        display_order: i,
        area_group,
        capacity,
        standard_minutes,
        effective_from: "2026-09-24",
        revision: "paper-2026-1",
      }),
    ),
    mv_nclean_instructions: ["2026-10-17", "2026-10-20", "2026-09-24"].map(
      (work_date) => ({
        ...common,
        id: work_date,
        work_date,
        instruction: work_date.startsWith("2026-10")
          ? "Today, vacuum the air-conditioning filter screens in every house."
          : "Today, clean the drains and shower wall and floor tiles, removing the pink residue.",
      }),
    ),
    mv_nclean_lost_entries: fixture.lost_entries.map((e) => ({
      ...common,
      snapshot_at: "2026-05-18T09:00:00+03:00",
      ...e,
      found_date: null,
      custody_from: e.recorded_handover_date,
      dispatch_date: "2026-05-18",
      batch_id: "NC-2026-05",
    })),
    mv_nclean_batches: [
      {
        ...common,
        snapshot_at: "2026-05-18T09:00:00+03:00",
        id: "NC-2026-05",
        coverage_from: "2026-05-04",
        coverage_to: "2026-05-17",
        dispatch_date: "2026-05-18",
        preparer: "NCLEAN",
        recipient: fixture.recipient.name,
        delivery_address: fixture.recipient.address,
        recipient_contact: fixture.recipient.contact,
        original_period: fixture.original_header.period,
        original_week: fixture.original_header.week,
      },
    ],
  };
}

// The typed contract is derived from the bundled fixture shape. All-null/date fields
// have explicit types.
const dateFields = new Set([
  "service_date",
  "work_date",
  "effective_from",
  "recorded_handover_date",
  "found_date",
  "custody_from",
  "dispatch_date",
  "coverage_from",
  "coverage_to",
]);
const timestampFields = new Set(["snapshot_at", "job_start", "job_finish"]);
const numericFields = new Set([
  "quantity",
  "source_column",
  "pack4_factor",
  "pack2_factor",
  "pack4_units",
  "pack2_units",
  "elapsed_minutes",
  "standard_minutes",
  "display_order",
  "original_week",
]);
export const NCLEAN_CONTRACTS: Record<string, DataContract> =
  Object.fromEntries(
    Object.entries(createNcleanData()).map(([name, rows]) => [
      name,
      {
        source: name.replace("mv_", ""),
        label: name.replace("mv_nclean_", "NClean ").replaceAll("_", " "),
        grain: "One stable source or sample record",
        key: "id",
        columns: Object.fromEntries(
          Object.keys(rows[0]).map((key) => [
            key,
            {
              label: (
                {
                  unit_id: "Property Unit",
                  group_name: "Reporting group",
                  vap: "VAP",
                } as Record<string, string>
              )[key],
              type: dateFields.has(key)
                ? "DATE"
                : timestampFields.has(key)
                  ? "TIMESTAMP"
                  : numericFields.has(key)
                    ? "DOUBLE"
                    : typeof rows[0][key] === "boolean"
                      ? "BOOLEAN"
                      : "VARCHAR",
              nullable: rows.some((row) => row[key] === null),
              lineage: "Bundled NClean fixture",
              description: key
                .replaceAll("_", " ")
                .replace(/^\w/, (c) => c.toUpperCase()),
            },
          ]),
        ),
      } satisfies DataContract,
    ]),
  );
